// Copyright 2025, University of Maryland, All Rights Reserved
import { getWorkspacesApi } from './api';
import type {
  UserProfileResponse,
  UpdateUserRequest,
  UserOrgMembership,
  UserWorkspaceMembership
} from '../types/user';
import { setImpersonationTarget } from './impersonation-session';


export type UsersApi = ReturnType<typeof useUsersApi>;

// Hook-based API for internal use
export const useUsersApi = () => {
  const apiInstance = getWorkspacesApi();

  return {
    getAllUsers: async (): Promise<UserProfileResponse[]> => {
      console.log('Fetching all users');
      const response = await apiInstance.get<UserProfileResponse[]>(`/admin/users`);
      return response.data;
    },

    getOrgUsers: async (org_id: string): Promise<UserProfileResponse[]> => {
      console.log('Fetching users for org:', org_id);
      const response = await apiInstance.get<UserProfileResponse[]>(`/admin/organizations/${org_id}/users`);
      return response.data;
    },

    createUser: async (user_id: string): Promise<UserProfileResponse> => {
      console.log('Creating user:', user_id);
      const response = await apiInstance.post<UserProfileResponse>(`/admin/users`, {
        user_id: user_id,
        is_global_admin: false
      });
      return response.data;
    },

    updateUser: async (data: UpdateUserRequest): Promise<UserProfileResponse> => {
      console.log('Updating user:', data.userId);
      const response = await apiInstance.put<UserProfileResponse>(`/admin/users/${data.userId}`, data);
      return response.data;
    },

    deleteUser: async (user_id: string): Promise<void> => {
      console.log('Deleting user:', user_id);
      await apiInstance.delete(`/admin/users/${user_id}`);
    },

    getUser: async (user_id: string): Promise<UserProfileResponse> => {
      console.log('Fetching user profile:', user_id);
      const response = await apiInstance.get<UserProfileResponse>(`/admin/users/${user_id}`);
      return response.data;
    },

    getUserOrganizations: async (user_id: string): Promise<UserOrgMembership[]> => {
      console.log('Fetching user organizations:', user_id);
      const response = await apiInstance.get<UserOrgMembership[]>(`/admin/users/${user_id}/organizations`);
      return response.data;
    },

    getUserWorkspacesInOrg: async (user_id: string, org_id: string): Promise<UserWorkspaceMembership[]> => {
      console.log('Fetching user workspaces in org:', { user_id, org_id });
      const response = await apiInstance.get<UserWorkspaceMembership[]>(
        `/admin/users/${user_id}/organizations/${org_id}/workspaces`
      );
      return response.data;
    },

    addUserToOrg: async (org_id: string, user_id: string, roles: string[]): Promise<void> => {
      console.log('Adding user to organization:', { org_id, user_id, roles });
      await apiInstance.post(`/admin/organizations/${org_id}/users`, { user_id, roles });
    },

    removeUserFromOrg: async (org_id: string, user_id: string): Promise<void> => {
      console.log('Removing user from organization:', { org_id, user_id });
      await apiInstance.delete(`/admin/organizations/${org_id}/users/${user_id}`);
    },

    updateUserOrgRoles: async (org_id: string, user_id: string, roles: string[]): Promise<void> => {
      console.log('Updating user org roles:', { org_id, user_id, roles });
      await apiInstance.put(`/admin/organizations/${org_id}/users/${user_id}/roles`, { roles });
    },

    addUserToWorkspace: async (org_id: string, ws_id: string, user_id: string, role: string): Promise<void> => {
      console.log('Adding user to workspace:', { org_id, ws_id, user_id, role });
      await apiInstance.post(`/admin/organizations/${org_id}/workspaces/${ws_id}/users`, { user_id, role });
    },

    removeUserFromWorkspace: async (org_id: string, ws_id: string, user_id: string): Promise<void> => {
      console.log('Removing user from workspace:', { org_id, ws_id, user_id });
      await apiInstance.delete(`/admin/organizations/${org_id}/workspaces/${ws_id}/users/${user_id}`);
    },

    updateUserWorkspaceRole: async (org_id: string, ws_id: string, user_id: string, role: string): Promise<void> => {
      console.log('Updating user workspace role:', { org_id, ws_id, user_id, role });
      await apiInstance.put(`/admin/organizations/${org_id}/workspaces/${ws_id}/users/${user_id}/role`, { role });
    },

    setUserImpersonation: async (targetUserId: string | null): Promise<void> => {
      console.log('Setting user impersonation:', { targetUserId });
      await apiInstance.put<void>(`/admin/users/impersonation`, {
        target_user_id: targetUserId
      });
      setImpersonationTarget(targetUserId);
    },
  };
};

// Standalone API functions for use outside React components
export const fetchUserProfile = async (): Promise<UserProfileResponse> => {
  const apiInstance = getWorkspacesApi();
  try {
    const response = await apiInstance.get<UserProfileResponse>(`/user/profile`);
    return response.data;
  } catch (error) {
    console.error('[Workspaces API] Error fetching user profile:', error);
    throw error;
  }
};
